import { Controller } from 'framework/core';
import { Singleton } from 'framework/di';

import { DomHelper } from '../../helpers/dom.helper';
import { DTOHelper } from '../../helpers/dto.helper';
import { WCAGHelper } from '../../helpers/wcag.helper';
import { AppService } from '../app/app.service';
import { CartController } from '../cart/cart.controller';
import type { PreOrderVariant } from '../pre-order/pre-order.model';
import { PreOrderService } from '../pre-order/pre-order.service';
import type { ProductItemVariant, QuickAddToCartItem } from './product.model';
import { ProductSwatchService } from './product-swatch.service';

interface VariantSelectionState {
  selectedVariant: ProductItemVariant | null;
}

@Singleton()
export class QuickAddToCartController extends Controller {
  protected prevSelectedPopup: HTMLElement | null = null;
  protected cartController: CartController | null = null;
  protected SUBMIT_KEYS = ['Enter', ' ', 'Spacebar'];
  protected SUCCESS_POPUP_CLOSE_DELAY = 500;

  constructor(
    protected domHelper: DomHelper,
    protected wcagHelper: WCAGHelper,
    protected appService: AppService,
    protected swatchService: ProductSwatchService,
    protected dtoHelper: DTOHelper,
    protected preOrderService: PreOrderService
  ) {
    super();
  }

  protected async getCartController(): Promise<CartController> {
    if (!this.cartController) {
      this.cartController = await this.resolve(CartController);
    }
    return this.cartController;
  }

  async open(item: QuickAddToCartItem, target: HTMLElement | null): Promise<void> {
    if (this.prevSelectedPopup) {
      this.prevSelectedPopup.style.display = 'none';
    }

    const selectedId = item?.variant_id || item?.productId;
    if (!selectedId) return;

    const productItem = this.domHelper.validateHTMLElement(
      target?.closest('.boost-sd__product-item')
    );

    if (!productItem) return;

    const popupSelection = productItem.querySelector<HTMLElement>('.boost-sd__popup-select-option');
    if (!popupSelection) return;

    popupSelection.style.display = 'block';

    this.initPopupSelection(popupSelection, productItem);

    const cartCtrl = await this.getCartController();
    cartCtrl.focusedElementBeforeMiniCart = document.activeElement as HTMLElement;

    this.wcagHelper.focusFirstElement(popupSelection, 'label, button');

    const handlePopupTrapFocus = (e: KeyboardEvent) => {
      this.wcagHelper.trapFocus(
        e,
        popupSelection,
        'label, button',
        '.boost-sd__popup-select-option-close-btn'
      );
    };

    popupSelection.addEventListener('keydown', handlePopupTrapFocus);

    this.prevSelectedPopup = popupSelection;
  }

  close(target: HTMLElement): void {
    const popupSelection = this.domHelper.validateHTMLElement(
      target.closest('.boost-sd__popup-select-option')
    );

    if (!popupSelection) return;

    popupSelection.style.display = 'none';

    const productItem = target.closest('.boost-sd__product-item');
    const selectOptionBtn = productItem?.querySelector<HTMLElement>(
      '.boost-sd__button--select-option'
    );
    selectOptionBtn?.focus();
  }

  handleClickOutside(target: HTMLElement): void {
    if (target.closest('.boost-sd__button--select-option')) {
      return;
    }

    const miniPopup = target
      ?.closest('.boost-sd__product-item')
      ?.querySelector('.boost-sd__popup-select-option');

    const miniPopupOpening = document.querySelector<HTMLElement>(
      '.boost-sd__popup-select-option[style="display: block;"]'
    );

    if (!miniPopup && miniPopupOpening) {
      miniPopupOpening.style.display = 'none';
    }
  }

  protected initPopupSelection(popupSelection: HTMLElement, productItem: HTMLElement): void {
    const productSwatches = popupSelection.querySelectorAll('.boost-sd__product-swatch');
    const addToCartButton = popupSelection.querySelector<HTMLButtonElement>(
      '.boost-sd__btn-add-to-cart'
    );
    const addToCartButtonText = popupSelection.querySelector<HTMLElement>(
      '.boost-sd__btn-add-to-cart .boost-sd__button-text'
    );

    const productData = this.dtoHelper.safeParseJSON<{
      variants?: ProductItemVariant[] | string;
    }>(productItem.getAttribute('data-product') || '');

    let productVariants: ProductItemVariant[] | null;

    if (typeof productData?.variants === 'string') {
      productVariants = this.dtoHelper.safeParseJSON<ProductItemVariant[]>(productData.variants);
    } else {
      productVariants = productData?.variants as ProductItemVariant[] | null;
    }

    if (!productVariants || productVariants.length === 0) return;

    const state: VariantSelectionState = {
      selectedVariant: productVariants[0],
    };

    this.resetSelectedOptions(popupSelection);

    productSwatches.forEach((swatch) => {
      const swatchOptions = swatch.querySelectorAll('.boost-sd__product-swatch-option');

      swatchOptions.forEach((option) => {
        this.initializeOptionState(option as HTMLElement, state, popupSelection);

        option.addEventListener('click', () => {
          this.handleOptionSelect(option as HTMLElement, popupSelection, productVariants!, state);
        });

        option.addEventListener('keyup', (e: Event) => {
          const keyEvent = e as KeyboardEvent;
          if (this.SUBMIT_KEYS.includes(keyEvent.key)) {
            this.handleOptionSelect(option as HTMLElement, popupSelection, productVariants!, state);
          }
        });
      });
    });

    addToCartButton?.addEventListener('click', async () => {
      await this.handleAddToCart(state.selectedVariant, addToCartButton, popupSelection);
    });

    this.updateButtonState(addToCartButton, addToCartButtonText, state.selectedVariant);
  }

  protected resetSelectedOptions(popupSelection: HTMLElement): void {
    const selectedSwatchLabels = popupSelection.querySelectorAll('.boost-sd__radio-label');

    selectedSwatchLabels.forEach((label) => {
      label.classList.remove('boost-sd__radio-label--selected');
    });
  }

  protected initializeOptionState(
    option: HTMLElement,
    state: VariantSelectionState,
    popupSelection: HTMLElement
  ): void {
    const label = option.querySelector('.boost-sd__radio-label');
    if (!label) return;

    const selectedVariant = state.selectedVariant;
    if (!selectedVariant?.title) return;

    const selectedVariantTitle = selectedVariant.title;
    const optionTitle = label.textContent?.trim() || '';

    const selectedVariantOptions: string[] = [];
    let remainingTitle = selectedVariantTitle;

    popupSelection.querySelectorAll('.boost-sd__radio-label').forEach((el) => {
      const text = el.textContent?.trim() || '';
      if (remainingTitle.startsWith(text)) {
        selectedVariantOptions.push(text);
        remainingTitle = remainingTitle
          .replace(text, '')
          .trim()
          .replace(/^\/\s*/, '');
      }
    });

    const parentSwatch = option.closest('.boost-sd__product-swatch');
    const allSwatches = [...popupSelection.querySelectorAll('.boost-sd__product-swatch')];
    const index = parentSwatch ? allSwatches.indexOf(parentSwatch) : -1;

    if (
      index !== -1 &&
      selectedVariantOptions[index]?.toLowerCase() === optionTitle.toLowerCase()
    ) {
      label.classList.add('boost-sd__radio-label--selected');
    }
  }

  protected handleOptionSelect(
    option: HTMLElement,
    popupSelection: HTMLElement,
    productVariants: ProductItemVariant[],
    state: VariantSelectionState
  ): void {
    const swatchOptions = option.parentElement?.parentElement?.querySelectorAll(
      '.boost-sd__product-swatch-option'
    );

    swatchOptions?.forEach((opt) => {
      const label = opt.querySelector('.boost-sd__radio-label');
      label?.classList.remove('boost-sd__radio-label--selected');
    });

    const label = option.querySelector('.boost-sd__radio-label');
    label?.classList.add('boost-sd__radio-label--selected');

    const title = this.buildVariantTitle(popupSelection);

    const selectedVariant = productVariants.find((variant) => variant.title === title) || {
      available: false,
      id: '',
      price: null,
      title: '',
    };

    state.selectedVariant = selectedVariant as ProductItemVariant;

    const addToCartButton = popupSelection.querySelector<HTMLButtonElement>(
      '.boost-sd__btn-add-to-cart'
    );
    const addToCartButtonText = popupSelection.querySelector<HTMLElement>(
      '.boost-sd__btn-add-to-cart .boost-sd__button-text'
    );

    this.updateButtonState(addToCartButton, addToCartButtonText, state.selectedVariant);
  }

  protected buildVariantTitle(popupSelection: HTMLElement): string {
    const selectedSwatchOptions = popupSelection.querySelectorAll(
      '.boost-sd__radio-label--selected'
    );

    let title = '';
    selectedSwatchOptions.forEach((option, i) => {
      title += option.textContent?.trim() || '';

      if (i < selectedSwatchOptions.length - 1) {
        title += ' / ';
      }
    });

    return title;
  }

  protected updateButtonState(
    button: HTMLButtonElement | null,
    buttonText: HTMLElement | null,
    variant: ProductItemVariant | null
  ): void {
    if (!button) return;

    const translation = this.appService.translation?.productItem as
      | Record<string, string>
      | undefined;
    const isPreOrder = this.isPreOrderVariant(variant);
    const isSoldOut = !variant?.available;

    if (isPreOrder) {
      button.disabled = false;
      if (buttonText && !buttonText.classList.contains('boost-sd__button--icon')) {
        // Delegate to PreOrderService for variant-specific button text
        const preOrderVariant = variant as PreOrderVariant;
        buttonText.textContent =
          this.preOrderService?.getPreOrderButtonText(preOrderVariant) ||
          translation?.preOrderLabel ||
          'Pre-order';
      }
    } else if (isSoldOut) {
      button.disabled = true;
      if (buttonText && !buttonText.classList.contains('boost-sd__button--icon')) {
        buttonText.textContent = translation?.soldoutLabel || 'Sold out';
      }
    } else {
      button.disabled = false;
      if (buttonText && !buttonText.classList.contains('boost-sd__button--icon')) {
        buttonText.textContent = translation?.atcAvailableLabel || 'Add to cart';
      }
    }

    if (variant?.id) {
      button.setAttribute('data-variant-id', String(variant.id));
    }
  }

  protected isPreOrderVariant(variant: ProductItemVariant | null): boolean {
    if (!variant) return false;

    // Delegate to PreOrderService for consistent detection (checks variant.preorder)
    if (this.preOrderService?.isPreOrderVariant(variant as PreOrderVariant)) return true;

    // Fallback: check Shopify inventory policy for continue-selling with no stock
    const inventoryPolicy = variant.inventory_policy;
    const inventoryQuantity = variant.inventory_quantity;

    return inventoryPolicy === 'continue' && inventoryQuantity <= 0;
  }

  protected async handleAddToCart(
    variant: ProductItemVariant | null,
    button: HTMLButtonElement,
    popupSelection: HTMLElement
  ): Promise<void> {
    if (!variant || !variant.id) return;

    const isValid = await this.validateSelection(variant);
    if (!isValid) return;

    const cartParams = this.buildCartParams(variant);
    const cartController = await this.getCartController();
    const result = await cartController.addToCart(cartParams, button);

    if (result?.success) {
      setTimeout(() => {
        popupSelection.style.display = 'none';
      }, this.SUCCESS_POPUP_CLOSE_DELAY);
    }
  }

  protected async validateSelection(variant: ProductItemVariant): Promise<boolean> {
    return Boolean(variant.id);
  }

  protected buildCartParams(variant: ProductItemVariant): {
    productId: string;
    quantity: number;
    selling_plan?: string;
    properties?: Record<string, string>;
  } {
    // Delegate to PreOrderService for pre-order cart item construction
    if (this.isPreOrderVariant(variant) && this.preOrderService) {
      const preOrderItem = this.preOrderService.buildPreOrderCartItem(
        variant as PreOrderVariant,
        1
      );
      return {
        ...preOrderItem,
        productId: String(preOrderItem.productId),
      };
    }

    return {
      productId: String(variant.id),
      quantity: 1,
    };
  }
}
